import { useState } from "react";
import { Download, Scissors } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import appIcon from "@/assets/app-icon.png";
import featureGraphic512 from "/feature-graphic-1024x500.png";

const StoreAssets = () => {
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);

  const downloadImage = (imageUrl: string, filename: string) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const cropImageTo500Height = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Set canvas to exact Google Play dimensions
      canvas.width = 1024;
      canvas.height = 500;
      
      // Crop from center: remove 6px from top and 6px from bottom
      ctx.drawImage(img, 0, 6, 1024, 500, 0, 0, 1024, 500);
      
      // Convert to blob and create download URL
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          setCroppedImageUrl(url);
        }
      }, 'image/png');
    };
    
    img.src = featureGraphic512;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Google Play Store Assets
          </h1>
          <p className="text-muted-foreground">
            Download the generated assets for your store listing
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* App Icon */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                App Icon
                <span className="text-sm font-normal text-muted-foreground">
                  (512x512)
                </span>
              </CardTitle>
              <CardDescription>
                Use this as your app icon in the Google Play Console
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-center p-4 bg-muted/50 rounded-lg">
                <img 
                  src={appIcon} 
                  alt="App Icon" 
                  className="w-32 h-32 rounded-2xl shadow-lg"
                />
              </div>
              <Button 
                onClick={() => downloadImage(appIcon, 'am-i-ok-app-icon-512x512.png')}
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Download App Icon (PNG)
              </Button>
              <p className="text-xs text-muted-foreground">
                Format: PNG, Size: 512x512px, Required for Google Play
              </p>
            </CardContent>
          </Card>

          {/* Feature Graphic */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Feature Graphic
                <span className="text-sm font-normal text-muted-foreground">
                  (1024x500)
                </span>
              </CardTitle>
              <CardDescription>
                Use this as your feature graphic in the Google Play Console
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-center p-4 bg-muted/50 rounded-lg">
                <img 
                  src={croppedImageUrl || featureGraphic512} 
                  alt="Feature Graphic" 
                  className="max-w-full h-auto rounded-lg shadow-lg"
                  style={{ maxHeight: '150px' }}
                />
              </div>
              
              {!croppedImageUrl && (
                <Button 
                  onClick={cropImageTo500Height}
                  variant="outline"
                  className="w-full"
                >
                  <Scissors className="w-4 h-4 mr-2" />
                  Crop to 1024x500 (Google Play Size)
                </Button>
              )}
              
              {croppedImageUrl && (
                <Button 
                  onClick={() => downloadImage(croppedImageUrl, 'am-i-ok-feature-graphic-1024x500.png')}
                  className="w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Feature Graphic (PNG)
                </Button>
              )}
              
              <p className="text-xs text-muted-foreground">
                Format: PNG, Size: 1024x500px, Required for Google Play
                {!croppedImageUrl && " (Click crop button first)"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Store Listing Text */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Store Listing Text</CardTitle>
            <CardDescription>
              Copy this text for your Google Play Console listing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">Short Description (80 chars)</h3>
              <div className="p-3 bg-muted/50 rounded-lg">
                <code className="text-sm">
                  Health information & triage tool with emergency alerts and care guidance
                </code>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="ml-2"
                  onClick={() => navigator.clipboard.writeText("Health information & triage tool with emergency alerts and care guidance")}
                >
                  Copy
                </Button>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Full Description</h3>
              <div className="p-3 bg-muted/50 rounded-lg max-h-48 overflow-y-auto">
                <div className="text-sm whitespace-pre-wrap">
                  {`**Am I OK? - Health Information & Triage Tool**

Am I OK? is a health information and triage application that helps users assess symptoms and understand when to seek medical care. This educational tool provides guidance on health concerns while emphasizing the importance of professional medical consultation.

**🚨 EMERGENCY & FIRST AID FEATURES:**
• Emergency symptom recognition for critical conditions (heart attack, stroke, severe bleeding)
• Immediate alerts when urgent medical attention may be needed
• Direct access to emergency contact information and local services
• Quick triage to help determine appropriate level of care

**🧠 CLINICAL DECISION SUPPORT:**
• Structured symptom assessment with evidence-based scoring
• Risk factor evaluation using established medical criteria
• Information sourced from peer-reviewed medical literature (PubMed)
• WHO ICD-11 classification system integration for educational reference

**📊 HEALTH INFORMATION ORGANIZATION:**
• User-friendly results presentation with clear explanations
• Educational content about health conditions and symptoms
• Guidance on appropriate healthcare provider specialties
• Structured care planning recommendations

**🔬 EDUCATIONAL MEDICAL RESOURCES:**
Educational information sourced from:
- Published medical research and guidelines
- WHO International Classification of Diseases (ICD-11)
- FDA-approved drug safety information
- Established clinical practice standards

**📱 SIMPLE USER FLOW:**
1. Quick emergency symptom screening (30 seconds)
2. Comprehensive health information assessment (5-10 minutes)
3. Educational results with clear risk communication
4. Guidance on next steps and appropriate care settings
5. Personal health information tracking over time

**🔒 PRIVACY & DATA PROTECTION:**
- Complete user control over personal health data
- Secure data storage with industry-standard encryption
- No sharing of personal information without explicit consent
- Full data deletion capabilities available anytime

**⚠️ IMPORTANT MEDICAL DISCLAIMER:**
Am I OK? is strictly a health information and educational triage tool. It does NOT provide medical diagnoses, treatment recommendations, or replace professional medical care. All users should consult qualified healthcare professionals for medical advice, diagnosis, and treatment decisions. In medical emergencies, contact emergency services immediately.

**Designed for:**
✓ Individuals seeking health education and information
✓ People preparing questions for medical appointments
✓ Those wanting guidance on appropriate care settings
✓ Users learning about health conditions and symptoms
✓ Individuals tracking health information over time

Educational health information to support informed healthcare decisions.`}
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="mt-2"
                onClick={() => navigator.clipboard.writeText(`**Am I OK? - Health Information & Triage Tool**

Am I OK? is a health information and triage application that helps users assess symptoms and understand when to seek medical care. This educational tool provides guidance on health concerns while emphasizing the importance of professional medical consultation.

**🚨 EMERGENCY & FIRST AID FEATURES:**
• Emergency symptom recognition for critical conditions (heart attack, stroke, severe bleeding)
• Immediate alerts when urgent medical attention may be needed
• Direct access to emergency contact information and local services
• Quick triage to help determine appropriate level of care

**🧠 CLINICAL DECISION SUPPORT:**
• Structured symptom assessment with evidence-based scoring
• Risk factor evaluation using established medical criteria
• Information sourced from peer-reviewed medical literature (PubMed)
• WHO ICD-11 classification system integration for educational reference

**📊 HEALTH INFORMATION ORGANIZATION:**
• User-friendly results presentation with clear explanations
• Educational content about health conditions and symptoms
• Guidance on appropriate healthcare provider specialties
• Structured care planning recommendations

**🔬 EDUCATIONAL MEDICAL RESOURCES:**
Educational information sourced from:
- Published medical research and guidelines
- WHO International Classification of Diseases (ICD-11)
- FDA-approved drug safety information
- Established clinical practice standards

**📱 SIMPLE USER FLOW:**
1. Quick emergency symptom screening (30 seconds)
2. Comprehensive health information assessment (5-10 minutes)
3. Educational results with clear risk communication
4. Guidance on next steps and appropriate care settings
5. Personal health information tracking over time

**🔒 PRIVACY & DATA PROTECTION:**
- Complete user control over personal health data
- Secure data storage with industry-standard encryption
- No sharing of personal information without explicit consent
- Full data deletion capabilities available anytime

**⚠️ IMPORTANT MEDICAL DISCLAIMER:**
Am I OK? is strictly a health information and educational triage tool. It does NOT provide medical diagnoses, treatment recommendations, or replace professional medical care. All users should consult qualified healthcare professionals for medical advice, diagnosis, and treatment decisions. In medical emergencies, contact emergency services immediately.

**Designed for:**
✓ Individuals seeking health education and information
✓ People preparing questions for medical appointments
✓ Those wanting guidance on appropriate care settings
✓ Users learning about health conditions and symptoms
✓ Individuals tracking health information over time

Educational health information to support informed healthcare decisions.`)}
              >
                Copy Full Description
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StoreAssets;